import React, { useMemo, useState } from 'react';
import {
  StyleSheet,
  View,
  Text,
  Platform,
  TouchableOpacity,
} from 'react-native';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';

import { useInstallmentPlansV2Query } from 'apollo/queries/autogenerate/hooks';
import { installmentPlanDataFragment } from 'apollo/queries/autogenerate/operations';
import {
  InstallmentPlan,
  InstallmentPlanKind,
  InstallmentPlanStatus,
} from 'apollo/queries/autogenerate/schemas';
import ListItemDivider from 'shared/modules/usage/components/atoms/ListItemDivider';
import InstallmentItem from 'shared/modules/usage/components/molecules/InstallmentItem';
import {
  getInstallmentAmountandInstallmentLengthLabel,
  getUpComingInstallmentLabel,
} from 'shared/modules/usage/utils/charge-utils';
import { formatYearMonthDate } from 'shared/modules/usage/utils/utils';
import NavigationBar from 'web/modules/common/components/molecules/NavigationBar';
import routes from 'web/navigation/routes';

export enum NpayCategory {
  NPay = 'nPay',
  Apple = 'apple',
  Multipay = 'multipay',
}

export default function ContractsSummaryScreen() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(NpayCategory.NPay);
  const isBigScreen = useMediaQuery({ minWidth: 768 });

  // Returns YYYY-MM-DD
  // And sets the date to 1
  const getFirstDateOfCurrentMonth = () => {
    const date = new Date();

    return `${date.getFullYear()}-${(date.getMonth() + 1)
      .toString()
      .padStart(2, '0')}-01`;
  };

  const { data: V2Data, loading } = useInstallmentPlansV2Query({
    variables: {
      status: [InstallmentPlanStatus.ACTIVE, InstallmentPlanStatus.COMPLETED],
      kinds: [
        InstallmentPlanKind.THREE_PAY,
        InstallmentPlanKind.SIX_PAY,
        InstallmentPlanKind.TWELVE_PAY,
        InstallmentPlanKind.APPLE3_MONTHS,
        InstallmentPlanKind.APPLE6_MONTHS,
        InstallmentPlanKind.APPLE12_MONTHS,
        InstallmentPlanKind.APPLE24_MONTHS,
        InstallmentPlanKind.APPLE36_MONTHS,
        InstallmentPlanKind.APPLE48_MONTHS,
      ],
      dueDateAfter: getFirstDateOfCurrentMonth(),
      limit: 700,
    },
  });

  const [threePays, sixPays, twelvePays] = useMemo(
    () =>
      !loading && V2Data
        ? V2Data?.installmentPlansV2.reduce(
            (
              accumulator: [
                installmentPlanDataFragment[],
                installmentPlanDataFragment[],
                installmentPlanDataFragment[]
              ],
              plan
            ) => {
              if (plan.kind === InstallmentPlanKind.THREE_PAY) {
                accumulator[0].push(plan); // push plan to threePays array
              }

              if (plan.kind === InstallmentPlanKind.SIX_PAY) {
                accumulator[1].push(plan); // push plan to sixPays array
              }

              if (plan.kind === InstallmentPlanKind.TWELVE_PAY) {
                accumulator[2].push(plan); // push plan to twelvePays array
              }

              return accumulator;
            },
            [[], [], []]
          )
        : [[], [], []],
    [V2Data, loading]
  );

  if (!V2Data) {
    return null;
  }

  return (
    <View style={[styles.screen, isBigScreen && styles.screenBigScreen]}>
      <View
        style={[styles.container, isBigScreen && styles.containerBigScreen]}
      >
        <NavigationBar
          title="分割あと払いの詳細"
          onBackButtonPress={() => navigate(-1)}
          id="navigation-bar"
        />
        <View style={styles.categoryView}>
          <View>
            <View
              style={
                tab === NpayCategory.NPay
                  ? styles.activeCategory
                  : styles.category
              }
            >
              <Text style={styles.categoryText}>3・6・12回あと払い</Text>
            </View>
          </View>
          <View>
            <TouchableOpacity
              onPress={() => {
                setTab(NpayCategory.Multipay);
                navigate(routes.authenticated.usage.multiPayContracts);
              }}
              style={
                tab === NpayCategory.Multipay
                  ? styles.activeCategory
                  : styles.category
              }
            >
              <Text style={styles.categoryText}>
                分割払い（2020年10月以前）
              </Text>
            </TouchableOpacity>
          </View>
        </View>
        {tab === NpayCategory.NPay && (
          <View>
            <Text style={styles.sectionTitle}>３回あと払い</Text>
            {threePays?.length !== 0 ? (
              <View style={styles.card}>
                {threePays?.map(
                  (installment: installmentPlanDataFragment, index: number) => {
                    return (
                      installment && (
                        <View key={index}>
                          <InstallmentItem
                            installment={installment as InstallmentPlan}
                            amount={installment.amount}
                            date={formatYearMonthDate(
                              installment.createdAt,
                              'en'
                            )}
                            description={getUpComingInstallmentLabel(
                              installment as InstallmentPlan
                            )}
                            installmentBreakdown={getInstallmentAmountandInstallmentLengthLabel(
                              installment as InstallmentPlan
                            )}
                          />
                          {index !== threePays.length - 1 && (
                            <ListItemDivider />
                          )}
                        </View>
                      )
                    );
                  }
                )}
              </View>
            ) : (
              <View style={styles.messageContainer}>
                <Text style={styles.emptyMessage}>
                  ３回あと払い支払額はありません。
                </Text>
              </View>
            )}
            <Text style={styles.sectionTitle}>6回あと払い</Text>
            {sixPays?.length !== 0 ? (
              <View style={styles.card}>
                {sixPays?.map(
                  (installment: installmentPlanDataFragment, index: number) => {
                    return (
                      installment && (
                        <View key={index}>
                          <InstallmentItem
                            installment={installment as InstallmentPlan}
                            amount={installment.amount}
                            date={formatYearMonthDate(
                              installment.createdAt,
                              'en'
                            )}
                            description={getUpComingInstallmentLabel(
                              installment as InstallmentPlan
                            )}
                            installmentBreakdown={getInstallmentAmountandInstallmentLengthLabel(
                              installment as InstallmentPlan
                            )}
                          />
                          {index !== sixPays.length - 1 && <ListItemDivider />}
                        </View>
                      )
                    );
                  }
                )}
              </View>
            ) : (
              <View style={styles.messageContainer}>
                <Text style={styles.emptyMessage}>
                  6回あと払い支払額はありません。
                </Text>
              </View>
            )}
            <Text style={styles.sectionTitle}>12回あと払い</Text>
            {twelvePays?.length !== 0 ? (
              <View style={styles.card}>
                {twelvePays?.map(
                  (installment: installmentPlanDataFragment, index: number) => {
                    return (
                      installment && (
                        <View key={index}>
                          <InstallmentItem
                            installment={installment as InstallmentPlan}
                            amount={installment.amount}
                            date={formatYearMonthDate(
                              installment.createdAt,
                              'en'
                            )}
                            description={getUpComingInstallmentLabel(
                              installment as InstallmentPlan
                            )}
                            installmentBreakdown={getInstallmentAmountandInstallmentLengthLabel(
                              installment as InstallmentPlan
                            )}
                          />
                          {index !== twelvePays.length - 1 && (
                            <ListItemDivider />
                          )}
                        </View>
                      )
                    );
                  }
                )}
              </View>
            ) : (
              <View style={styles.messageContainer}>
                <Text style={styles.emptyMessage}>
                  12回あと払い支払額はありません。
                </Text>
              </View>
            )}
          </View>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    alignItems: 'center',
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    overflow: 'scroll',
    paddingBottom: 24,
    padding: 16,
  },
  screenBigScreen: {
    padding: 24,
  },
  container: {
    width: '100%',
  },
  containerBigScreen: {
    maxWidth: 960,
  },
  category: {
    borderRadius: 16,
    height: 32,
    paddingHorizontal: 16,
    alignItems: 'center',
    marginRight: 8,
    flexDirection: 'row',
    backgroundColor: '#e1e1e1',
  },
  activeCategory: {
    backgroundColor: '#fff',
    borderRadius: 16,
    height: 32,
    paddingHorizontal: 16,
    alignItems: 'center',
    flexDirection: 'row',
    marginRight: 8,
  },
  categoryText: {
    fontSize: 12,
    lineHeight: 16,
    color: '#2a2e32',
    fontWeight: '700',
  },
  categoryView: {
    marginBottom: 24,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  sectionTitle: {
    fontSize: 12,
    lineHeight: 14,
    fontWeight: '600',
    color: '#1c1c1c',
  },

  card: {
    marginVertical: 16,
    minHeight: 72,
    justifyContent: 'center',
    backgroundColor: '#fff',
    borderRadius: 8,
    ...Platform.select({
      android: {
        elevation: 2,
      },
      ios: {
        shadowColor: 'rgba(0, 0, 0, 0.08)',
        shadowOpacity: 1,
        shadowRadius: 8,
        shadowOffset: {
          width: 0,
          height: 2,
        },
      },
    }),
  },
  messageContainer: {
    alignItems: 'center',
    backgroundColor: '#fff',
    borderRadius: 8,
    marginVertical: 16,
  },
  emptyMessage: {
    fontSize: 12,
    fontWeight: '300',
    lineHeight: 16,
    color: '#a1aab0',
    margin: 24,
  },
});
